import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Heart, MessageCircle, Box, Palette, Star, Truck, Gift } from "lucide-react";
import { api, WHATSAPP_LINK } from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_3D_IMAGE = "https://customer-assets.emergentagent.com/job_ala-3d-prints/artifacts/rttm2vi4_figuras-para-imprimir-3D.png";

const Home = () => {
  const [products, setProducts] = useState([]);
  const [products3d, setProducts3d] = useState([]);
  const [productsPap, setProductsPap] = useState([]);
  const [heroImage, setHeroImage] = useState(HERO_3D_IMAGE);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setProducts(r.data));
    api.get("/products?section=3d").then((r) => setProducts3d(r.data));
    api.get("/products?section=papeleria").then((r) => setProductsPap(r.data));
    api.get("/settings").then((r) => {
      if (r.data?.hero_image_url) setHeroImage(r.data.hero_image_url);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-cream">
      {/* HERO */}
      <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 dotted opacity-40" />
        <div className="absolute top-20 -right-20 w-[400px] h-[400px] rounded-full bg-yolk/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-mint/30 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-yolk px-4 py-2 mb-6 border-2 border-black rounded-full">
              <Sparkles className="h-4 w-4 text-black" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-black">
                Imprenta artesanal · Copiapó
              </span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold tracking-tighter text-brand-ink leading-[0.92]">
              Creamos
              <br />
              <span className="font-accent italic font-semibold text-mint-deep">lo que</span>
              <br />
              imaginas<span className="text-yolk">.</span>
            </h1>

            <p className="mt-6 text-lg text-gray-700 max-w-xl leading-relaxed">
              Figuras 3D de Pokémon, dragones, juguetes articulados y anime. Papelería kawaii personalizada:
              libros para colorear, agendas, stickers y vinilos.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_LINK("Hola, quiero cotizar mi proyecto personalizado")}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-whatsapp"
                className="btn-chunky inline-flex items-center justify-center gap-3 bg-yolk text-black px-8 py-5 text-base"
              >
                <MessageCircle className="h-5 w-5" />
                COTIZA TU PROYECTO PERSONALIZADO
              </a>
              <Link
                to="/3d"
                data-testid="hero-cta-3d"
                className="btn-chunky inline-flex items-center justify-center gap-3 bg-mint-deep text-white px-8 py-5 text-base"
              >
                Ver figuras 3D
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-mint-deep" /> Calidad premium</div>
              <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-yolk" /> Hecho con cariño</div>
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-mint-deep" /> Diseños únicos</div>
            </div>
          </div>

          <div className="relative animate-fade-up delay-200">
            <div className="relative">
              {/* Yolk frame */}
              <div className="absolute -inset-4 bg-yolk border-2 border-black" />
              <div className="relative aspect-[4/5] overflow-hidden border-2 border-black bg-mint-light">
                <img
                  src={heroImage}
                  alt="Figuras 3D ALA Graphics"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center center" }}
                />
              </div>
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white p-4 border-2 border-black max-w-[220px] z-10">
              <div className="flex items-center gap-2 mb-1">
                <Box className="h-4 w-4 text-mint-deep" />
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-mint-deep">3D Premium</span>
              </div>
              <p className="font-heading text-sm font-bold text-brand-ink">
                Pokémon · Dragones · Articulados
              </p>
            </div>

            <div className="absolute -top-4 right-4 bg-yolk p-3 border-2 border-black z-10 animate-bounce-slow">
              <Gift className="h-5 w-5 text-black" />
            </div>
          </div>
        </div>
      </section>

      {/* TWO SECTIONS: 3D + PAPELERIA */}
      <section className="relative py-16 md:py-24 bg-white border-y-2 border-black overflow-hidden">
        <div className="absolute inset-0 confetti opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Categorías ─</span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl font-bold tracking-tight text-brand-ink">
              Dos mundos, una <span className="font-accent italic text-mint-deep">imprenta</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/3d" data-testid="section-card-3d" className="group relative bg-mint-deep text-white border-2 border-black rounded-3xl p-8 md:p-10 hover:-translate-y-2 hover:rotate-[-0.5deg] hover:shadow-[10px_10px_0_0_#0A0A0A] transition-all overflow-hidden">
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-yolk/20 blur-2xl" />
              <Box className="h-10 w-10 mb-4" />
              <h3 className="font-heading text-3xl md:text-4xl font-bold mb-3">Figuras 3D</h3>
              <p className="text-white/80 mb-4 max-w-md">
                Pokémon coleccionables, dragones articulados, juguetes flexi, figuras de anime y merchandising.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {["Pokémon", "Dragones", "Articulados", "Anime", "Merch"].map((t) => (
                  <span key={t} className="text-xs bg-black/30 px-3 py-1 border border-white/30 rounded-full">{t}</span>
                ))}
              </div>
              <span className="inline-flex items-center gap-2 font-bold border-b-2 border-yolk pb-1">
                Ver figuras 3D <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
              </span>
            </Link>

            <Link to="/papeleria" data-testid="section-card-papeleria" className="group relative bg-yolk text-black border-2 border-black rounded-3xl p-8 md:p-10 hover:-translate-y-2 hover:rotate-[0.5deg] hover:shadow-[10px_10px_0_0_#0A0A0A] transition-all overflow-hidden">
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-mint-deep/30 blur-2xl" />
              <Palette className="h-10 w-10 mb-4" />
              <h3 className="font-heading text-3xl md:text-4xl font-bold mb-3">Papelería Kawaii</h3>
              <p className="text-black/80 mb-4 max-w-md">
                Libros para colorear personalizados, agendas kawaii, stickers troquelados y vinilos adhesivos.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {["Libros", "Agendas", "Stickers", "Vinilos"].map((t) => (
                  <span key={t} className="text-xs bg-black/15 px-3 py-1 border border-black/30 rounded-full">{t}</span>
                ))}
              </div>
              <span className="inline-flex items-center gap-2 font-bold border-b-2 border-black pb-1">
                Ver papelería <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="relative py-16 md:py-24 bg-white overflow-hidden">
        <div className="absolute inset-0 confetti opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Destacados ─</span>
              <h2 className="mt-3 font-heading text-4xl md:text-5xl font-bold tracking-tight text-brand-ink">
                Los más <span className="font-accent italic text-mint-deep">amados</span>
              </h2>
            </div>
            <Link to="/3d" data-testid="products-view-all" className="inline-flex items-center gap-2 text-sm font-heading font-bold text-brand-ink hover:text-mint-deep transition link-underline self-start md:self-auto">
              Ver todo el catálogo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {products.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative py-20 md:py-28 bg-brand-ink text-white overflow-hidden border-t-4 border-yolk">
        <div className="absolute inset-0 confetti opacity-30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ ¿Tienes una idea? ─</span>
          <h2 className="mt-4 font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight">
            Hagámosla <span className="font-accent italic text-mint">realidad</span>.
          </h2>
          <p className="mt-6 text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            Cuéntanos qué necesitas: figura 3D personalizada, libro para colorear con la foto de tu hijo,
            agenda kawaii, stickers de tu marca... lo hacemos.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={WHATSAPP_LINK("Hola, quiero cotizar mi proyecto personalizado")}
              target="_blank"
              rel="noreferrer"
              data-testid="cta-strip-whatsapp"
              className="btn-chunky inline-flex items-center justify-center gap-3 bg-yolk text-black px-8 py-5 text-base"
            >
              <MessageCircle className="h-5 w-5" />
              COTIZA TU PROYECTO PERSONALIZADO
            </a>
            <Link
              to="/cotizar"
              data-testid="cta-strip-form"
              className="btn-chunky inline-flex items-center justify-center gap-3 bg-mint-deep text-white px-8 py-5 text-base"
            >
              Formulario rápido
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="bg-white/5 border border-yolk/40 p-4 flex items-center gap-3">
              <Gift className="h-6 w-6 text-yolk shrink-0" />
              <p className="text-sm text-left text-white/90">Compras sobre <strong className="text-yolk">$20.000</strong> · Regalo sorpresa GRATIS</p>
            </div>
            <div className="bg-white/5 border border-mint/40 p-4 flex items-center gap-3">
              <Truck className="h-6 w-6 text-mint shrink-0" />
              <p className="text-sm text-left text-white/90">Delivery <strong className="text-mint">GRATIS</strong> sobre $30.000 en Copiapó</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
